'use client';

import { Combobox } from '@/components/ui/combobox';
import { fieldLabelClass } from '@/components/ui/form-control';
import { canonicalizeBcp47Locale } from '@/lib/locale';
import { EU_APP_LOCALES } from '@/i18n/eu-locales';
import { APP_COPY, type AppCopy } from '@/i18n/catalogs';
import { interpolateCopy, useChatCopy, useChatInterfaceLocale } from '@/lib/chat-i18n';

export interface LanguageOption {
  value: string;
  label: string;
  kind: 'automatic' | 'supported' | 'custom';
}

// All EU official languages are valid app-language settings. Catalog loading
// is language-keyed, so adding a translated catalog does not change this wire
// contract or the picker.
export const SUPPORTED_LANGUAGE_OPTIONS: ReadonlyArray<LanguageOption> = EU_APP_LOCALES.map(
  (value) => ({ value, label: value, kind: 'supported' }),
);

export const AUTOMATIC_LANGUAGE_OPTION: LanguageOption = {
  value: '',
  label: 'Automatic (browser language)',
  kind: 'automatic',
};

function matchingPreset(locale: string): LanguageOption | undefined {
  const likelyLocale = new Intl.Locale(locale).maximize().toString();
  return SUPPORTED_LANGUAGE_OPTIONS.find(
    (option) => new Intl.Locale(option.value).maximize().toString() === likelyLocale,
  );
}

/** Canonical form state: valid locales stay explicit; missing/invalid data becomes Automatic. */
export function normalizeLanguageFormValue(value: string | null | undefined): string {
  if (!value) return '';
  const canonical = canonicalizeBcp47Locale(value);
  if (!canonical) return '';
  return matchingPreset(canonical)?.value ?? canonical;
}

export interface LanguagePickerSession {
  value: string;
  initialValue: string;
}

/** Hydrate both controlled values from one canonical persisted value. */
export function languagePickerSession(
  storedValue: string | null | undefined,
): LanguagePickerSession {
  const value = normalizeLanguageFormValue(storedValue);
  return { value, initialValue: value };
}

/** Preserve the settings wire contract where Automatic is serialized as null. */
export function languageSettingValue(value: string): string | null {
  return normalizeLanguageFormValue(value) || null;
}

function languageName(locale: string, displayLocale: string): string | null {
  try {
    const language = new Intl.Locale(locale).language;
    return new Intl.DisplayNames([displayLocale], { type: 'language' }).of(language) ?? null;
  } catch {
    return null;
  }
}

/**
 * Keep the session's initially stored, valid BCP-47 locale selectable even
 * after another option is chosen. New choices stay deliberately guided while
 * the server's arbitrary-locale capability remains non-lossy for existing
 * settings.
 */
export function languageOptionsFor(
  value: string,
  initialValue: string = value,
  displayLocale = 'en',
  copy: AppCopy = APP_COPY.en,
): ReadonlyArray<LanguageOption> {
  const customLocales = [initialValue, value]
    .map(normalizeLanguageFormValue)
    .filter(
      (locale, index, locales) =>
        locale !== '' &&
        !SUPPORTED_LANGUAGE_OPTIONS.some((option) => option.value === locale) &&
        locales.indexOf(locale) === index,
    );

  return [
    { ...AUTOMATIC_LANGUAGE_OPTION, label: copy.automaticBrowserLanguage },
    ...SUPPORTED_LANGUAGE_OPTIONS.map((option) => ({
      ...option,
      label: `${languageName(option.value, displayLocale) ?? option.value} (${option.value})`,
    })),
    ...customLocales.map((locale) => {
      const name = languageName(locale, displayLocale);
      const label = name
        ? interpolateCopy(copy.previousLanguageChoice, { language: name, locale })
        : interpolateCopy(copy.previousLanguageChoiceCode, { locale });
      return { value: locale, label, kind: 'custom' as const };
    }),
  ];
}

/** Match the selected value to the option's canonical form without mutating settings. */
export function languagePickerValue(value: string): string {
  return normalizeLanguageFormValue(value);
}

export function LanguagePicker({
  value,
  initialValue,
  onChange,
  disabled,
}: {
  value: string;
  initialValue?: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  const copy = useChatCopy();
  const locale = useChatInterfaceLocale();
  const options = languageOptionsFor(value, initialValue, locale, copy);

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label className={fieldLabelClass} htmlFor="settings-language">
        {copy.appLanguage}
      </label>
      <Combobox
        id="settings-language"
        testId="settings-language-picker"
        label={copy.appLanguage}
        value={languagePickerValue(value)}
        options={options}
        disabled={disabled}
        onChange={onChange}
        describedBy="settings-language-help"
        className="border-border bg-background min-h-11 w-full min-w-0 rounded-sm border px-3 text-base sm:text-sm"
      />
      <div id="settings-language-help" className="text-muted-foreground text-xs leading-snug">
        {copy.appLanguageHelp}
      </div>
    </div>
  );
}
